export const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const calculatePatientAge = (dob) => {
  if (!dob) return 65;
  try {
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return 65;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age > 0 ? age : 65;
  } catch (e) {
    return 65;
  }
};

export const STANDARD_PROMOTIONS = {
  p88: {
    id: 'p88',
    name: '$88.88 Complete Pair',
    code: 'BSV24PR88',
    promoType: 'Package Price',
    baseStatus: 'ELIGIBLE',
    evaluate: (ctx) => {
      const isEligible = ctx.pairCount >= 1;
      const frameOverage = Math.max(0, ctx.framePrice - 100);
      const total = 88.88 + frameOverage + 40 + 45;
      const savings = Math.max(0, ctx.retailTotal - total);
      return {
        isEligible,
        status: isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE',
        statusType: isEligible ? 'good' : 'bad',
        retail: ctx.retailTotal,
        total,
        savings,
        lines: [
          ['Retail order', money(ctx.retailTotal), ''],
          ['Complete-pair promo base', '$88.88', ''],
          [`Frame over $100 allowance`, money(frameOverage), 'muted'],
          ['Polycarbonate upgrade', '$40.00', 'muted'],
          ['Satin AR upgrade', '$45.00', 'muted'],
          ['Promotion savings', `-${money(savings)}`, 'discount'],
        ],
        rule: 'Complete pair required. The $88.88 package pricing is applied with the $100 frame allowance; eligible material and coating upgrades follow their configured promotional pricing.',
      };
    },
  },
  p2: {
    id: 'p2',
    name: '2 for $79 SV / 2 for $179 Progressive',
    code: 'BSV24279SV',
    promoType: 'Package Price',
    baseStatus: 'NOT ELIGIBLE',
    evaluate: (ctx) => {
      const isEligible = ctx.pairCount >= 2;
      return {
        isEligible,
        status: isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE',
        statusType: isEligible ? 'good' : 'bad',
        retail: ctx.retailTotal,
        total: isEligible ? 179 : ctx.retailTotal,
        savings: isEligible ? Math.max(0, ctx.retailTotal - 179) : 0,
        lines: isEligible
          ? [
              ['Retail order', money(ctx.retailTotal), ''],
              ['Package Price', '$179.00', ''],
              ['Promotion savings', `-${money(ctx.retailTotal - 179)}`, 'discount'],
            ]
          : [
              ['Eligibility', 'Not eligible', 'muted'],
              ['Required', '2 Complete Pairs', ''],
              ['Current order', `${ctx.pairCount} Complete Pair${ctx.pairCount === 1 ? '' : 's'}`, ''],
            ],
        rule: `Requires two qualifying complete pairs on the same order. Current order contains ${ctx.pairCount} complete pair${ctx.pairCount === 1 ? '' : 's'}.`,
      };
    },
  },
  p50: {
    id: 'p50',
    name: '50% Off Complete Pair',
    code: 'BSV2450',
    promoType: 'Percent Off',
    baseStatus: 'ELIGIBLE',
    evaluate: (ctx) => {
      const isEligible = ctx.pairCount >= 1;
      const savings = ctx.retailTotal * 0.5;
      const total = ctx.retailTotal - savings;
      return {
        isEligible,
        status: isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE',
        statusType: isEligible ? 'good' : 'bad',
        retail: ctx.retailTotal,
        total,
        savings,
        lines: [
          ['Retail order', money(ctx.retailTotal), ''],
          ['Eligible promotion discount', '50%', 'muted'],
          ['Promotion savings', `-${money(savings)}`, 'discount'],
        ],
        rule: 'Complete pair required. Eligible components receive 50% off retail subject to configured exceptions such as Transitions, X-coded and non-discountable frames.',
      };
    },
  },
  pdec: {
    id: 'pdec',
    name: 'Decades Discount',
    code: 'BSV24DD',
    promoType: 'Age Based',
    baseStatus: 'ELIGIBLE',
    evaluate: (ctx) => {
      const isEligible = ctx.pairCount >= 1 && ctx.patientAge >= 10;
      let percent = Math.min(90, Math.floor(ctx.patientAge / 10) * 10);
      if (percent < 10) percent = 60;
      const frameOverage = Math.max(0, ctx.framePrice - 100);
      const discountableAmount = Math.max(0, ctx.retailTotal - frameOverage);
      const savings = discountableAmount * (percent / 100);
      const total = ctx.retailTotal - savings;
      return {
        isEligible,
        status: isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE',
        statusType: isEligible ? 'good' : 'bad',
        retail: ctx.retailTotal,
        total,
        savings,
        lines: [
          ['Retail order', money(ctx.retailTotal), ''],
          ['Patient age', `${ctx.patientAge}`, 'muted'],
          ['Age tier', `${percent}% off`, 'muted'],
          ['Frame overage', money(frameOverage), 'muted'],
          ['Promotion savings', `-${money(savings)}`, 'discount'],
        ],
        rule: `At age ${ctx.patientAge} the patient qualifies for the ${percent}% tier. The age discount applies to the configured eligible components and the first $100 of the frame; frame overage remains patient responsibility.`,
      };
    },
  },
  pkid: {
    id: 'pkid',
    name: 'Kids $69.99 / 2 for $99.99',
    code: 'BSV24KID69 / BSV24KID99',
    promoType: 'Package Price',
    baseStatus: 'NOT ELIGIBLE',
    evaluate: (ctx) => {
      const isEligible = ctx.patientAge <= 16 && ctx.pairCount >= 1;
      return {
        isEligible,
        status: isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE',
        statusType: isEligible ? 'good' : 'bad',
        retail: ctx.retailTotal,
        total: isEligible ? 69.99 : ctx.retailTotal,
        savings: isEligible ? Math.max(0, ctx.retailTotal - 69.99) : 0,
        lines: isEligible
          ? [
              ['Retail order', money(ctx.retailTotal), ''],
              ['Kids Package Price', '$69.99', ''],
              ['Promotion savings', `-${money(ctx.retailTotal - 69.99)}`, 'discount'],
            ]
          : [
              ['Eligibility', 'Not eligible', 'muted'],
              ['Required age', '16 or younger', ''],
              ['Patient age', `${ctx.patientAge}`, ''],
            ],
        rule: 'Requires age 16 or younger, a qualifying complete pair, polycarbonate base lens and EPP.',
      };
    },
  },
  pback: {
    id: 'pback',
    name: 'BACKTOSCHOOL26',
    code: 'BACKTOSCHOOL26',
    promoType: 'Package Price',
    baseStatus: 'NOT ELIGIBLE',
    evaluate: (ctx) => ({
      isEligible: false,
      status: 'NOT ELIGIBLE',
      statusType: 'bad',
      retail: ctx.retailTotal,
      total: ctx.retailTotal,
      savings: 0,
      lines: [
        ['Eligibility', 'Not eligible', 'muted'],
        ['Package price', '$99.00', ''],
        ['Required', 'Select Frames + AR', ''],
      ],
      rule: 'Configured as a $99 complete-pair code promotion requiring selected frame inventory and anti-reflective coating. Exact expiration/lens restrictions remain configuration items.',
    }),
  },
  pexam199: {
    id: 'pexam199',
    name: 'EXAM + GLASSES - $199 Package',
    code: 'BUNDLE PROMOTION',
    promoType: 'Bundle',
    baseStatus: 'NOT ELIGIBLE',
    evaluate: (ctx) => ({
      isEligible: false,
      status: 'NOT ELIGIBLE',
      statusType: 'bad',
      retail: ctx.retailTotal,
      total: ctx.retailTotal,
      savings: 0,
      lines: [
        ['Eligibility', 'Not eligible', 'muted'],
        ['Bundle price', '$199.00', ''],
        ['Required', 'Exam + qualifying complete pair', ''],
      ],
      rule: 'Requires the configured exam procedure and qualifying package frame/lens inventory.',
    }),
  },
  pinf20: {
    id: 'pinf20',
    name: 'Customer - 20% Off Complete Pair - Standard InFocus Promotion',
    code: 'SECOND PAIR PROMOTION',
    promoType: 'Second Pair Discount',
    baseStatus: 'NOT ELIGIBLE',
    evaluate: (ctx) => ({
      isEligible: false,
      status: 'NOT ELIGIBLE',
      statusType: 'bad',
      retail: ctx.retailTotal,
      total: ctx.retailTotal,
      savings: 0,
      lines: [
        ['Eligibility', 'Not eligible', 'muted'],
        ['Required', 'Second Complete Pair', ''],
        ['Insurance', 'No Insurance', ''],
      ],
      rule: 'Requires a second qualifying complete pair. Source frame-category wording still needs final confirmation.',
    }),
  },
  pinf50: {
    id: 'pinf50',
    name: 'Customer - 50% Off 2nd Complete Pair Purchase',
    code: 'SECOND PAIR PROMOTION',
    promoType: 'Second Pair Discount',
    baseStatus: 'NOT ELIGIBLE',
    evaluate: (ctx) => ({
      isEligible: false,
      status: 'NOT ELIGIBLE',
      statusType: 'bad',
      retail: ctx.retailTotal,
      total: ctx.retailTotal,
      savings: 0,
      lines: [
        ['Eligibility', 'Not eligible', 'muted'],
        ['Discount', '50%', ''],
        ['Required', 'Second Complete Pair + Standard Frames', ''],
      ],
      rule: 'Applies to the qualifying second complete pair using Standard Frames with No Insurance.',
    }),
  },
  pinfpkg: {
    id: 'pinfpkg',
    name: 'InFocus Package',
    code: 'PACKAGE PROMOTION',
    promoType: 'Package Price',
    baseStatus: 'NOT ELIGIBLE',
    evaluate: (ctx) => ({
      isEligible: false,
      status: 'NOT ELIGIBLE',
      statusType: 'bad',
      retail: ctx.retailTotal,
      total: ctx.retailTotal,
      savings: 0,
      lines: [
        ['Single Vision', '$129.00', ''],
        ['FT28', '$149.00', ''],
        ['Promotion PAL', '$179.00', ''],
        ['Standard AR', 'Included', 'muted'],
      ],
      rule: 'Package pricing depends on mapped lens type. Standard AR is included. Frame category/allowance must be configured.',
    }),
  },
  pannual: {
    id: 'pannual',
    name: 'ANNUAL SUPPLY - 20% OFF Plano Sunglasses',
    code: 'TRIGGERED PROMOTION',
    promoType: 'Triggered Discount',
    baseStatus: 'NOT ELIGIBLE',
    evaluate: (ctx) => ({
      isEligible: false,
      status: 'NOT ELIGIBLE',
      statusType: 'bad',
      retail: ctx.retailTotal,
      total: ctx.retailTotal,
      savings: 0,
      lines: [
        ['Eligibility', 'Not eligible', 'muted'],
        ['Trigger', '12-Month Contact Lens Supply', ''],
        ['Target', 'Plano Sunglasses - 20% off', ''],
      ],
      rule: 'Promotion becomes eligible only when a qualifying 12-month contact lens supply is present.',
    }),
  },
  pmilitary: {
    id: 'pmilitary',
    name: 'Military Discount - 40% Off Complete Pair',
    code: 'ELIGIBILITY PROMOTION',
    promoType: 'Eligibility Discount',
    baseStatus: 'NOT ELIGIBLE',
    evaluate: (ctx) => ({
      isEligible: false,
      status: 'NOT ELIGIBLE',
      statusType: 'bad',
      retail: ctx.retailTotal,
      total: ctx.retailTotal,
      savings: 0,
      lines: [
        ['Eligibility', 'Not verified', 'muted'],
        ['Discount', '40%', ''],
        ['Insurance', 'No Insurance', ''],
      ],
      rule: 'Requires active-service or retired-veteran eligibility plus a qualifying complete pair with No Insurance.',
    }),
  },
  pmgrlab: {
    id: 'pmgrlab',
    name: 'MANAGER DISCRETION - Lab Delay / Breakage',
    code: 'MANAGER ADJUSTMENT',
    promoType: 'Manager Adjustment',
    baseStatus: 'MANAGER ONLY',
    evaluate: (ctx) => ({
      isEligible: true,
      status: 'MANAGER ONLY',
      statusType: 'info',
      retail: ctx.retailTotal,
      total: Math.max(0, ctx.retailTotal - 25),
      savings: 25,
      lines: [
        ['Adjustment', '-$25.00', 'discount'],
        ['Approval', 'Manager Required', ''],
        ['Documentation', 'Patient Profile Note Required', ''],
      ],
      rule: 'Manual manager adjustment for lab delay/breakage.',
    }),
  },
  pmgrorder: {
    id: 'pmgrorder',
    name: 'MANAGER DISCRETION - Order Incorrect / Delay',
    code: 'MANAGER ADJUSTMENT',
    promoType: 'Manager Adjustment',
    baseStatus: 'MANAGER ONLY',
    evaluate: (ctx) => ({
      isEligible: true,
      status: 'MANAGER ONLY',
      statusType: 'info',
      retail: ctx.retailTotal,
      total: Math.max(0, ctx.retailTotal - 25),
      savings: 25,
      lines: [
        ['Adjustment', '-$25.00', 'discount'],
        ['Approval', 'Manager Required', ''],
        ['Documentation', 'Patient Profile Note Required', ''],
      ],
      rule: 'Manual manager adjustment for an incorrect or delayed order.',
    }),
  },
  pmgrsat: {
    id: 'pmgrsat',
    name: 'MANAGER DISCRETION - Customer Satisfaction',
    code: 'MANAGER ADJUSTMENT',
    promoType: 'Manager Adjustment',
    baseStatus: 'MANAGER ONLY',
    evaluate: (ctx) => ({
      isEligible: true,
      status: 'MANAGER ONLY',
      statusType: 'info',
      retail: ctx.retailTotal,
      total: Math.max(0, ctx.retailTotal - 25),
      savings: 25,
      lines: [
        ['Adjustment', '-$25.00', 'discount'],
        ['Approval', 'Manager Required', ''],
        ['Documentation', 'Patient Profile Note Required', ''],
      ],
      rule: 'Manual manager adjustment for customer satisfaction.',
    }),
  },
  pthankstd: {
    id: 'pthankstd',
    name: 'THANKYOU $50 OFF - Up to 50% off complete pair - Standard Frame',
    code: 'CARD PROMOTION',
    promoType: 'Card Promotion',
    baseStatus: 'NOT ELIGIBLE',
    evaluate: (ctx) => ({
      isEligible: false,
      status: 'NOT ELIGIBLE',
      statusType: 'bad',
      retail: ctx.retailTotal,
      total: ctx.retailTotal,
      savings: 0,
      lines: [
        ['Eligibility', 'Not eligible', 'muted'],
        ['Required', 'Standard Frame + Card', ''],
        ['Discount formula', 'TBD', ''],
      ],
      rule: 'Card must be presented. Exact relationship between “$50 OFF” and “up to 50% off” remains to be configured.',
    }),
  },
  pthanklux: {
    id: 'pthanklux',
    name: 'THANKYOU $50 OFF - Up to 20% off complete pair - Designer/Luxury Frame',
    code: 'CARD PROMOTION',
    promoType: 'Card Promotion',
    baseStatus: 'NOT ELIGIBLE',
    evaluate: (ctx) => ({
      isEligible: false,
      status: 'NOT ELIGIBLE',
      statusType: 'bad',
      retail: ctx.retailTotal,
      total: ctx.retailTotal,
      savings: 0,
      lines: [
        ['Eligibility', 'Not eligible', 'muted'],
        ['Required', 'Designer/Luxury Frame + Card', ''],
        ['Discount formula', 'TBD', ''],
      ],
      rule: 'Card must be presented. Exact relationship between “$50 OFF” and “up to 20% off” remains to be configured.',
    }),
  },
  premake0: {
    id: 'premake0',
    name: 'REMAKE -- No Charge',
    code: 'REMAKE ADJUSTMENT',
    promoType: 'Remake Adjustment',
    baseStatus: 'REMAKE ONLY',
    evaluate: (ctx) => ({
      isEligible: false,
      status: 'REMAKE ONLY',
      statusType: 'info',
      retail: ctx.retailTotal,
      total: 0,
      savings: ctx.retailTotal,
      lines: [
        ['Eligibility', 'Remake order only', 'muted'],
        ['Patient responsibility', '$0.00', ''],
        ['Original order reference', 'Required', ''],
      ],
      rule: 'For an authorized qualifying remake, remove applicable remake charges so patient responsibility is $0.',
    }),
  },
  premakept: {
    id: 'premakept',
    name: 'REMAKE -- Pt responsibility',
    code: 'REMAKE ADJUSTMENT',
    promoType: 'Remake Adjustment',
    baseStatus: 'REMAKE ONLY',
    evaluate: (ctx) => ({
      isEligible: false,
      status: 'REMAKE ONLY',
      statusType: 'info',
      retail: ctx.retailTotal,
      total: ctx.retailTotal,
      savings: 0,
      lines: [
        ['Eligibility', 'Remake order only', 'muted'],
        ['Patient responsibility', 'Difference only', ''],
        ['Original order reference', 'Required', ''],
      ],
      rule: 'Patient owes only the configured difference between the original order and replacement/remake configuration.',
    }),
  },
};

export const computeOrderContext = (order = {}, eyeglassFormValues = {}) => {
  const patient = order.patient_attributes || {};
  const patientAge = patient.age || calculatePatientAge(patient.dob);

  const eyeglasses = eyeglassFormValues.eyeglasses_attributes || order.eyeglasses_attributes || [];
  const activeEyeglasses = eyeglasses.filter((eg) => !eg._destroy);
  const pairCount = Math.max(1, activeEyeglasses.length);

  const firstEg = activeEyeglasses[0] || {};
  const framePrice = parseFloat(firstEg.frame_unit_price || firstEg.frame_price || 140) || 140;

  // Compute retail total from components
  let retailTotal = 0;
  activeEyeglasses.forEach((eg) => {
    const fPrice = parseFloat(eg.frame_unit_price || 0) || 0;
    let lPrice = 0;
    if (eg.lens_types_attributes && eg.lens_types_attributes.length > 0) {
      eg.lens_types_attributes.forEach((lt) => {
        lPrice += parseFloat(lt.unit_price || 0) || 0;
      });
    } else {
      lPrice += parseFloat(eg.lens_unit_price || 0) || 0;
    }
    const mPrice = parseFloat(eg.lens_material_unit_price || 0) || 0;
    let aPrice = 0;
    if (eg.eyeglass_addons_attributes) {
      eg.eyeglass_addons_attributes.forEach((addon) => {
        if (!addon._destroy) {
          aPrice += parseFloat(addon.unit_price || addon.price || 0) || 0;
        }
      });
    }
    retailTotal += fPrice + lPrice + mPrice + aPrice;
  });

  if (retailTotal <= 0) {
    retailTotal = 562; // standard sample order retail amount from prototype
  }

  return {
    patientAge,
    pairCount,
    framePrice,
    retailTotal,
  };
};

export const getProcessedPromotions = (context, apiPromotions = []) => {
  const list = [];

  // Add standard optical promotions
  Object.keys(STANDARD_PROMOTIONS).forEach((key) => {
    const def = STANDARD_PROMOTIONS[key];
    const evalResult = def.evaluate(context);
    list.push({
      id: def.id,
      name: def.name,
      code: def.code,
      promoType: def.promoType,
      isEligible: evalResult.isEligible,
      status: evalResult.status,
      statusType: evalResult.statusType,
      retail: evalResult.retail,
      total: evalResult.total,
      savings: evalResult.savings,
      lines: evalResult.lines,
      rule: evalResult.rule,
    });
  });

  // If there are custom promotions from API, merge them if not already represented
  if (Array.isArray(apiPromotions)) {
    apiPromotions.forEach((apiPromo) => {
      const existing = list.find(
        (p) => p.id === String(apiPromo.id) || (p.code && p.code.toLowerCase() === (apiPromo.code || '').toLowerCase())
      );
      if (!existing && apiPromo.name) {
        const isEligible = apiPromo.eligibile !== undefined ? apiPromo.eligibile : true;
        const savings = context.retailTotal * 0.2; // default 20%
        const total = context.retailTotal - savings;
        list.push({
          id: String(apiPromo.id),
          name: apiPromo.name,
          code: apiPromo.code ? `POS CODE: ${apiPromo.code}` : 'CUSTOM PROMOTION',
          promoType: apiPromo.promotion_type || 'Custom',
          isEligible,
          status: isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE',
          statusType: isEligible ? 'good' : 'bad',
          retail: context.retailTotal,
          total,
          savings,
          lines: [
            ['Retail order', money(context.retailTotal), ''],
            ['Promotion discount', money(savings), 'discount'],
          ],
          rule: apiPromo.rule_summary || apiPromo.notes || 'Configured promotion from Settings.',
        });
      }
    });
  }

  const eligibleCount = list.filter((p) => p.isEligible).length;

  return {
    promotions: list,
    eligibleCount,
    totalCount: list.length,
  };
};
